import { useEffect, useState } from "react";
import { useGetIdentity } from "@refinedev/core";
import { useQueryClient } from "@tanstack/react-query";
import { readMe } from "../providers/api.ts";
import { directus } from "@/providers/directus";
import { Modal } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/forms/Field";
import { notifySaved } from "@/lib/notifySaved";
import { invalidateResources } from "@/lib/cache";
import { validateProfileForm } from "@/lib/accountSettings";
import { usePreferences } from "@/hooks/usePreferences";
import { resolveModulesConfig, type ModulesConfig } from "@/lib/modules";
import { roleCanSee } from "@/providers/accessControl";

export function AccountSettingsDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { data: identity, refetch: refetchIdentity } = useGetIdentity<any>();
  const queryClient = useQueryClient();
  const { prefs, setPref } = usePreferences();
  const role = identity?.role ?? localStorage.getItem("cordite_role") ?? "";
  const modulesConfig: ModulesConfig = resolveModulesConfig(prefs?.modules as any, role);
  const canSeeFinance = roleCanSee("billable_line_items", role) || roleCanSee("expenses", role);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetTfa, setResetTfa] = useState(false);

  const [initial, setInitial] = useState({ firstName: "", lastName: "", email: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setPassword("");
    setConfirmPassword("");
    setResetTfa(false);

    const initFirst = identity?.first_name ?? "";
    const initLast = identity?.last_name ?? "";
    const initEmail = identity?.email ?? "";
    setFirstName(initFirst);
    setLastName(initLast);
    setEmail(initEmail);
    setInitial({ firstName: initFirst, lastName: initLast, email: initEmail });

    let cancelled = false;
    (async () => {
      try {
        const me: any = await directus.request(
          readMe({ fields: ["id", "email", "first_name", "last_name"] as any })
        );
        if (cancelled || !me) return;
        const f = me.first_name ?? "";
        const l = me.last_name ?? "";
        const em = me.email ?? "";
        setFirstName(f);
        setLastName(l);
        setEmail(em);
        setInitial({ firstName: f, lastName: l, email: em });
      } catch {
        // Fallback to cached identity values
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, identity?.first_name, identity?.last_name, identity?.email]);

  const isDirty = () =>
    firstName !== initial.firstName ||
    lastName !== initial.lastName ||
    email !== initial.email ||
    password.length > 0 ||
    confirmPassword.length > 0 ||
    resetTfa;

  async function handleSave() {
    if (!identity?.id) {
      setError("Could not resolve current user account.");
      return;
    }

    const validation = validateProfileForm({
      first_name: firstName,
      last_name: lastName,
      email,
      password,
      confirm_password: confirmPassword,
      reset_tfa: resetTfa,
    });

    if (!validation.ok || !validation.payload) {
      setError(validation.error || "Invalid profile details.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      await directus.request(() => ({
        method: "PATCH",
        path: `/portal/users/${identity.id}/admin-manage`,
        body: JSON.stringify(validation.payload),
        headers: { "Content-Type": "application/json" },
      }));

      await refetchIdentity();
      await invalidateResources(queryClient, ["directus_users"]);
      notifySaved("Account settings updated");
      onClose();
    } catch (e: any) {
      setError(e?.errors?.[0]?.message || e?.message || "Failed to update account settings.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      isDirty={isDirty}
      onSave={handleSave}
      title="Account Settings"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </>
      }
    >
      {error && (
        <div className="mb-3 rounded border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-1">
        <Field label="First name">
          <Input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First name"
            autoComplete="given-name"
          />
        </Field>
        <Field label="Last name">
          <Input
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Last name"
            autoComplete="family-name"
          />
        </Field>
      </div>

      <Field
        label="Email / Username"
        required
        hint="Used to sign in to Cordite."
      >
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          autoComplete="username"
        />
      </Field>

      <div className="mt-4 pt-3 border-t border-border">
        <div className="text-xs font-semibold text-foreground mb-2">Change Password</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="New password" hint="Leave blank to keep current password.">
            <Input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
            />
          </Field>
          <Field label="Confirm new password">
            <Input
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repeat new password"
            />
          </Field>
        </div>
      </div>

      <div className="mt-3 rounded-md border border-border p-3 bg-secondary/30">
        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={resetTfa}
            onChange={(e) => setResetTfa(e.target.checked)}
          />
          <div>
            <div className="text-xs font-medium text-foreground">Reset Two-Factor Authentication (2FA)</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              Clears your current authenticator secret so you can enroll a new device on your next sign-in.
            </div>
          </div>
        </label>
      </div>

      <div className="mt-4 pt-3 border-t border-border">
        <div className="text-xs font-semibold text-foreground mb-1">UI Modules &amp; Navigation</div>
        <div className="text-[11px] text-muted-foreground mb-2.5">
          Enable or disable optional workspace modules in your sidebar and dashboard.
        </div>
        <div className="space-y-2">
          <label className="flex items-start gap-2.5 rounded-md border border-border p-2.5 bg-card hover:bg-secondary/30 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 cursor-pointer"
              checked={modulesConfig.expenses}
              onChange={(e) => {
                setPref("modules", { ...modulesConfig, expenses: e.target.checked });
                notifySaved(e.target.checked ? "Expenses module enabled" : "Expenses module hidden");
              }}
            />
            <div>
              <div className="text-xs font-medium text-foreground">Expenses Module</div>
              <div className="text-[11px] text-muted-foreground">
                Show &ldquo;Submit Expense&rdquo; in the sidebar and expense tracking cards on your dashboard.
              </div>
            </div>
          </label>
          {canSeeFinance && (
            <label className="flex items-start gap-2.5 rounded-md border border-border p-2.5 bg-card hover:bg-secondary/30 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 cursor-pointer"
                checked={modulesConfig.finance}
                onChange={(e) => {
                  setPref("modules", { ...modulesConfig, finance: e.target.checked });
                  notifySaved(e.target.checked ? "Finance module enabled" : "Finance module hidden");
                }}
              />
              <div>
                <div className="text-xs font-medium text-foreground">Finance &amp; Billing Module</div>
                <div className="text-[11px] text-muted-foreground">
                  Show the Finance section (Funding, Billable Items, Monthly Invoicing, Reporting) in the sidebar.
                </div>
              </div>
            </label>
          )}
        </div>
      </div>
    </Modal>
  );
}
